import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, UserCheck, MapPin, Video, CalendarDays, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Página pública de resposta à convocatória (token no URL, sem login).
// Suporta auto-resposta via ?r=sim|nao|procuracao (botões do email).
export default function ConvocationRespond() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proxyName, setProxyName] = useState("");
  const [showProxy, setShowProxy] = useState(false);
  const proxyRef = useRef(null);
  const autoDone = useRef(false);

  const respond = async (answer, proxy) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/convocations/token/${token}/respond`,
        { answer, proxy_name: proxy || null });
      setData(r.data);
      setShowProxy(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Não foi possível registar a resposta.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/convocations/token/${token}`);
        setData(r.data);
        setProxyName(r.data.proxy_name || "");
        // Auto-resposta vinda dos botões do email
        const auto = params.get("r");
        if (auto && !autoDone.current) {
          autoDone.current = true;
          if (auto === "sim") await respond("yes");
          else if (auto === "nao") await respond("no");
          else if (auto === "procuracao") setShowProxy(true);
        }
      } catch (e) {
        setError(e?.response?.status === 404
          ? "Convocatória não encontrada ou link inválido."
          : "Erro a carregar a convocatória.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (showProxy) proxyRef.current?.focus(); }, [showProxy]);

  if (error) {
    return (
      <section className="container-page py-20 text-center" data-testid="convocation-error">
        <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="container-page py-20 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-[hsl(var(--muted-foreground))]" />
      </section>
    );
  }

  const answered = data.response;

  return (
    <section className="container-page py-12 lg:py-16 max-w-3xl" data-testid="convocation-page">
      <div className="overline">Convocatória · {data.kind_label}</div>
      <h1 className="display-heading text-3xl sm:text-4xl mt-3">{data.title}</h1>
      <span className="accent-rule mt-6 block" />

      <div className="mt-6 space-y-2 text-sm">
        <p className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[hsl(var(--accent))]" />
          <strong>{data.date_pt}</strong>{data.time ? ` às ${data.time}` : ""}
        </p>
        {data.location && (
          <p className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[hsl(var(--accent))]" /> {data.location}
          </p>
        )}
        {data.meeting_url && (
          <p className="flex items-center gap-2">
            <Video className="h-4 w-4 text-[hsl(var(--accent))]" />
            <a href={data.meeting_url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] underline break-all">{data.meeting_url}</a>
          </p>
        )}
      </div>

      {data.agenda && (
        <div className="mt-6 bg-[hsl(var(--secondary))] border-l-2 border-[hsl(var(--accent))] p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Ordem de trabalhos</div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{data.agenda}</div>
        </div>
      )}

      <div className="mt-8 bg-white border border-[hsl(var(--border))] p-6" data-testid="convocation-answer-box">
        <p className="text-sm">
          Olá, <strong>{data.member_name}</strong>.{" "}
          {answered ? "A sua resposta está registada — pode alterá-la aqui:" : "Por favor confirme a sua presença:"}
        </p>

        {answered && (
          <div className="mt-3 text-sm" data-testid="convocation-current-answer">
            {answered === "yes" && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Presença confirmada</span>}
            {answered === "no" && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800"><XCircle className="h-4 w-4" /> Não pode comparecer</span>}
            {answered === "proxy" && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800"><UserCheck className="h-4 w-4" /> Far-se-á representar por {data.proxy_name}</span>}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => respond("yes")} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#093630] disabled:opacity-60"
            data-testid="answer-yes">
            <CheckCircle2 className="h-4 w-4" /> Confirmo presença
          </button>
          <button onClick={() => respond("no")} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#8A3B3B] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#6d2e2e] disabled:opacity-60"
            data-testid="answer-no">
            <XCircle className="h-4 w-4" /> Não posso comparecer
          </button>
          <button onClick={() => setShowProxy((v) => !v)} disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[hsl(var(--accent))] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:opacity-90 disabled:opacity-60"
            data-testid="answer-proxy-toggle">
            <UserCheck className="h-4 w-4" /> Far-me-ei representar
          </button>
        </div>

        {showProxy && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="proxy-form">
            <input ref={proxyRef} value={proxyName} onChange={(e) => setProxyName(e.target.value)}
              placeholder="Nome de quem o(a) representará"
              className="flex-1 min-w-[220px] bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm"
              data-testid="proxy-name-input" />
            <button onClick={() => proxyName.trim() ? respond("proxy", proxyName.trim()) : null} disabled={busy || !proxyName.trim()}
              className="px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] font-semibold disabled:opacity-50"
              data-testid="proxy-submit">
              Registar procuração
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
